import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

export interface InvoicesContextValue {
  currentURLHashWithBookId: string;
  breadcrumbs: Array<{ pathname: string; breadcrumbTitle: string }>;
  moreLinks: Array<{ label: string; url: string }>;
  navigateToEditInvoice: (args: {
    invoiceId: string;
    editInvoiceRouteState?: Record<string, unknown>;
  }) => void;
  buildInvoiceHref: (invoiceId: string) => string;
  /**
   * Only set where a recurring template is reachable. The list leaves the
   * template name as plain text when it is unset.
   */
  buildRecurringInvoiceHref?: (recurringInvoiceId: string) => string;
  navigateToCreateInvoice: () => void;
  navigateToCreateInvoiceFromRecurringInvoice: (args: {
    recurringInvoiceId: string;
  }) => void;
  /**
   * Opens a new recurring template for the given invoice. The invoice itself
   * travels in `invoiceInitialisationValuesStore` for hosts that open the React
   * page; classic reads it back from the id.
   */
  navigateToCreateRecurringInvoiceFromInvoice?: (args: {
    invoiceId: string;
  }) => void;
  navigateToInvoicesList: () => void;
  navigateToRecurringTransactionsList?: () => void;
}

const InvoicesContext = createContext<InvoicesContextValue | null>(null);

export function useInvoicesContext(): InvoicesContextValue {
  const context = useContext(InvoicesContext);

  if (!context) {
    throw new Error(
      'useInvoicesContext must be used within an InvoicesProvider'
    );
  }

  return context;
}

export function InvoicesProvider(props: {
  value: InvoicesContextValue;
  children: ReactNode;
}) {
  return (
    <InvoicesContext.Provider value={props.value}>
      {props.children}
    </InvoicesContext.Provider>
  );
}
